import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    HostListener,
    Inject,
    Injector,
    OnInit,
    signal
} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {UserRole} from '../../enums/user-role';
import {getGuestStartPageUrl, ROUTE_REDIRECTS_TOKEN, RouteRedirects} from '../../models/route-redirects';
import {OnboardingMenuItemComponent} from '../../pages/onboarding-page/onboarding-menu-item';
import {IS_DETACHED_WIDGET} from '../../pages/widget-page/detached-widget-injection';
import {UserService} from '../../services/user.service';
import {WindowRefService} from '../../services/window-ref.service';
import {AuthService} from '../auth/auth.service';
import {destroy$} from '../destroyable';
import {DynamicComponentRef} from '../dynamic-component';
import {ModalService} from '../modal';
import {MainMenuDropdownComponent} from './main-menu-dropdown/main-menu-dropdown.component';
import {MainMenuItem} from './main-menu-item';

const SCREEN_WIDTH_VARIABLE = '--transitionScreenWidth';

@Component({
    selector: 'main-menu',
    templateUrl: 'main-menu.component.html',
    styleUrls: ['main-menu.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        '[class.-is-open]': 'this.$dropdownMenu()',
        '[class.-hidden]': 'this.$isHiddenForGuest()'
    }
})
export class MainMenuComponent implements OnInit {

    public readonly $dropdownMenu = signal<DynamicComponentRef<MainMenuDropdownComponent> | undefined>(undefined);

    public readonly destroy$ = destroy$();

    public readonly $menuItems = signal<MainMenuItem[]>([]);

    public readonly $isDashboard = signal(false);
    public readonly $isDetachedWidget = signal(false);

    /**
     * Tells, that the menu should be collapsed to a single button.
     * Otherwise, it should be displayed as expanded.
     */
    public readonly $isContentOverflowMenu = signal(true);

    public readonly $isAuth = signal(!!this.authService.userRole);

    public readonly $isMenuAllowed = computed(() =>
        !this.$isDetachedWidget()
        && (this.$isAuth() || this.$isDashboard())
    );

    public readonly $isMenuExpandable = computed(() =>
        this.$isMenuAllowed()
        && (this.$isDashboard() || this.$isContentOverflowMenu())
    );

    public readonly $isMenuConstantlyExpanded = computed(() =>
        this.$isMenuAllowed()
        && !this.$isDashboard() && !this.$isContentOverflowMenu()
    );

    public readonly $isHiddenForGuest = computed(() =>
        !this.$isAuth() && this.$isMenuAllowed() && !this.$menuItems().length
    );

    private readonly windowWidth$ = new Subject<number>();

    private readonly dashboardUrl = this.getDashboardUrl();

    constructor(
        private elementRef: ElementRef,
        private authService: AuthService,
        private userService: UserService,
        private modalService: ModalService,
        private windowRefService: WindowRefService,

        @Inject(ROUTE_REDIRECTS_TOKEN)
        private routeRedirects: RouteRedirects,

        @Inject(IS_DETACHED_WIDGET)
        public readonly isDetachedWidget: boolean,

        private router: Router,
        private injector: Injector
    ) {
        this.listenToAuth();
        this.updateActiveRouteFlags();

        try {
            this.listenRouteChanges();
        } catch (e) {
            console.error(e);
        }
    }

    @HostListener('click')
    public openDropdown(): void {
        if (!this.$dropdownMenu() && this.$isMenuExpandable()) {
            const dropdownMenu = this.modalService.open(MainMenuDropdownComponent, {
                injector:    this.injector,
                destroyWhen: this.destroy$,
                onDestroy:   () => this.$dropdownMenu.set(undefined),
                configure:   {
                    targetElement: this.elementRef,
                    menuItems:     this.$menuItems(),
                    logout:        this.$isAuth()
                                       ? () => this.authService.logout()
                                       : undefined
                }
            });

            this.$dropdownMenu.set(dropdownMenu);
        }
    }

    @HostListener('window:resize')
    public onWindowResize(): void {
        this.windowWidth$.next(window.innerWidth);
    }

    public readonly logoutFn = () => this.authService.logout();

    public ngOnInit(): void {
        /**
         * Get transition screen width from CSS variable
         */
        const transitionScreenWidth = parseInt(
            getComputedStyle(this.elementRef.nativeElement).getPropertyValue(SCREEN_WIDTH_VARIABLE), 0
        );

        /**
         * Handle window resize
         */
        const handleWindowResize = (innerWidth: number) => {
            this.$isContentOverflowMenu.set(innerWidth < transitionScreenWidth);

            if (this.$dropdownMenu() && !this.$isMenuExpandable()) {
                this.$dropdownMenu().destroy();
            }
        };

        /**
         * Get init menu state
         */
        handleWindowResize(this.windowRefService.nativeWindow.innerWidth);

        /**
         * Listen window resize
         */
        this.windowWidth$.pipe(
            distinctUntilChanged(),
            tap(handleWindowResize),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private listenRouteChanges(): void {
        this.router.events.pipe(
            tap(event => {
                if (event instanceof NavigationEnd) {
                    this.updateActiveRouteFlags();
                }
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private updateActiveRouteFlags(): void {
        const url = this.router.url;

        const isDashboard = this.dashboardUrl === url.split('?')[0]; // TODO: make better way
        this.$isDashboard.set(isDashboard);

        const isDetachedWidget = /^\/widget\b/.test(url);
        this.$isDetachedWidget.set(isDetachedWidget);
    }

    private createMenuItems(): MainMenuItem[] {
        const user = this.userService.user;
        const userRole = user && user.role;
        const isTrader = userRole === UserRole.TRADER;
        const isTraderAdmin = userRole === UserRole.TRADER_ADMIN;
        const hasCompany = !!(user && user.companyId);

        const conditionalMenuItems = (condition: boolean, ...items: MainMenuItem[]): MainMenuItem[] =>
            condition
                ? items
                : [];

        return [
            {
                label:                   'components.main-menu.dashboard',
                icon:                    '#main-menu-dashboard',
                routerLink:              [this.dashboardUrl],
                routerLinkActiveOptions: {exact: true}
            },
            ...conditionalMenuItems(isTraderAdmin && hasCompany,
                {
                    label:      'components.main-menu.accounts',
                    icon:       '#main-menu-accounts',
                    routerLink: ['/accounts']
                },
                {
                    label:      'components.main-menu.user_management',
                    icon:       '#main-menu-user-management',
                    routerLink: ['/user-management']
                }
            ),
            ...conditionalMenuItems(isTrader || isTraderAdmin,
                {
                    label:         'components.main-menu.onboarding',
                    icon:          '#main-menu-onboarding',
                    itemComponent: OnboardingMenuItemComponent,
                    routerLink:    ['/onboarding']
                }
            ),
            ...conditionalMenuItems(isTrader || isTraderAdmin,
                {
                    label:      'components.main-menu.api_keys',
                    icon:       '#main-menu-api-keys',
                    routerLink: ['/api-keys']
                }
            )
        ];
    }

    private listenToAuth(): void {
        this.userService.user$.pipe(
            tap(user => {
                const isAuth = !!user;
                this.$isAuth.set(isAuth);

                const menuItems = isAuth
                    ? this.createMenuItems()
                    : [];

                this.$menuItems.set(menuItems);
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private getDashboardUrl(): string {
        const userRole = this.authService.userRole;

        return userRole
            ? this.routeRedirects.DASHBOARD_ROUTES[userRole]
            : getGuestStartPageUrl(this.routeRedirects);
    }

}
